import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SectionTitle from "@/components/feature/SectionTitle";
import VideoCard from "@/components/feature/VideoCard";
import GalleryCard from "@/components/feature/GalleryCard";
import NewsCard from "@/components/feature/NewsCard";
import TopicRow from "@/components/feature/TopicRow";
import { usePageMeta } from "@/hooks/usePageMeta";
import { videos } from "@/mocks/videos";
import { featuredGalleries } from "@/mocks/galleries";
import { featuredNews } from "@/mocks/news";
import { hotTopics } from "@/mocks/topics";
import { formatViews } from "@/utils/format";

const hero1 =
  "https://readdy.ai/api/search-image?query=Cinematic%20dark%20lounge%20with%20deep%20burgundy%20velvet%20curtains%2C%20amber%20pendant%20lights%2C%20soft%20smoke%20haze%2C%20luxurious%20atmosphere%2C%20warm%20shadows%2C%20no%20people%2C%20editorial%20film%20still%2C%20high%20detail&width=1600&height=900&seq=adult-hero-01&orientation=landscape";
const hero2 =
  "https://readdy.ai/api/search-image?query=Artistic%20abstract%20composition%20of%20flowing%20dark%20red%20silk%20with%20amber%20rim%20light%20on%20a%20black%20background%2C%20elegant%20cinematic%20texture%2C%20high%20detail&width=1600&height=900&seq=adult-hero-02&orientation=landscape";
const hero3 =
  "https://readdy.ai/api/search-image?query=Moody%20rooftop%20bar%20at%20night%20with%20amber%20lanterns%20and%20deep%20wine%20red%20upholstery%2C%20city%20bokeh%20lights%20behind%2C%20cinematic%20wide%20shot%2C%20no%20people%2C%20high%20detail&width=1600&height=900&seq=adult-hero-03&orientation=landscape";

const slides = [
  {
    id: "v1",
    kicker: "本周焦点 · 剧情",
    title: "霓虹夜色",
    description: "一条没有尽头的走廊，一次隔着玻璃的相遇。克制的镜头语言，适合戴上耳机慢慢看。",
    image: hero1,
    meta: "18 分钟 · 1080P · 12.8 万次播放",
  },
  {
    id: "v4",
    kicker: "编辑推荐 · 欧美",
    title: "午夜爵士",
    description: "开到凌晨三点的酒吧，三段现场演奏，麦克风始终没有人使用。",
    image: hero2,
    meta: "32 分钟 · 4K · 15.3 万次播放",
  },
  {
    id: "v8",
    kicker: "深夜片单 · 日韩",
    title: "城市夜行",
    description: "雨后的柏油路反着霓虹，第一视角穿过整座城市。几乎没有剪辑断点。",
    image: hero3,
    meta: "19 分钟 · 1080P · 8.9 万次播放",
  },
];

const quickCategories = [
  { label: "剧情", icon: "ri-book-open-line", desc: "完整叙事" },
  { label: "情侣", icon: "ri-heart-3-line", desc: "双人日常" },
  { label: "唯美", icon: "ri-moon-clear-line", desc: "氛围优先" },
  { label: "欧美", icon: "ri-global-line", desc: "海外作品" },
  { label: "日韩", icon: "ri-earth-line", desc: "东亚风格" },
  { label: "独立制片", icon: "ri-film-line", desc: "小众实验" },
];

const columns = [
  { label: "夜色专题", desc: "霓虹、雨夜与城市灯光" },
  { label: "黑白影像", desc: "没有颜色的高级感" },
  { label: "独立制片", desc: "小团队的长镜头" },
  { label: "访谈与手记", desc: "创作者怎么说" },
];

const stats = [
  { value: "1,280+", label: "演示内容条目" },
  { value: "每日", label: "内容更新频率" },
  { value: "36", label: "社区进行中话题" },
];

export default function Home() {
  usePageMeta({
    title: "成人网 - 成人娱乐内容与资讯平台",
    description:
      "成人网提供丰富的成人娱乐内容、相关资讯与话题，涵盖视频、图片及社区交流，满足用户多样化的娱乐需求。",
    keywords: "成人网,成人娱乐,成人视频,成人资讯,成人社区,情色内容,成人图片",
  });

  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const slide = slides[active];
  const popularVideos = [...videos].sort((a, b) => b.views - a.views).slice(0, 5);

  return (
    <div>
      <section className="relative h-[480px] w-full overflow-hidden bg-background-100 md:h-[560px]">
        {slides.map((item, index) => (
          <img
            key={item.id}
            src={item.image}
            alt={`${item.title} - 成人网焦点推荐`}
            title={`${item.title} 成人视频推荐`}
            className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-1000 ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/92 via-background-950/65 to-background-950/25"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>

        <div className="container-page relative flex h-full items-center">
          <div className="max-w-2xl pb-10 pt-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent-500/50 bg-background-950/40 px-3 py-1 text-[11px] tracking-wide text-accent-400">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500 animate-soft-pulse"></span>
              {slide.kicker}
            </span>
            <h1 className="mt-5 font-heading text-[34px] font-semibold leading-tight text-foreground-950 md:text-[52px]">
              {slide.title}
            </h1>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              {slide.description}
            </p>
            <p className="mt-3 text-xs text-foreground-500">{slide.meta}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/video-detail/${slide.id}`}
                className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-play-fill"></i>
                查看详情
              </Link>
              <Link
                to="/video-category"
                className="flex items-center gap-2 rounded-md border border-foreground-700/40 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-400 whitespace-nowrap"
              >
                浏览全部视频
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 right-5 hidden items-center gap-3 md:flex">
          <div className="mr-2 flex items-center gap-1.5">
            {slides.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActive(index)}
                aria-label={`切换到第 ${index + 1} 张推荐`}
                className={`h-1 rounded-full transition-all cursor-pointer ${
                  index === active ? "w-8 bg-accent-500" : "w-4 bg-foreground-700/40"
                }`}
              ></button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setActive((prev) => (prev - 1 + slides.length) % slides.length)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground-700/30 text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500 cursor-pointer"
            aria-label="上一张推荐"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <button
            type="button"
            onClick={() => setActive((prev) => (prev + 1) % slides.length)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground-700/30 text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500 cursor-pointer"
            aria-label="下一张推荐"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </section>

      <div className="container-page -mt-2 pb-4">
        <section className="hide-scrollbar flex gap-3 overflow-x-auto pb-2">
          {quickCategories.map((item) => (
            <Link
              key={item.label}
              to={`/video-category?cat=${encodeURIComponent(item.label)}`}
              className="group flex min-w-[150px] flex-1 items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3.5 transition-colors hover:border-accent-500/60"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-500/15 text-lg text-primary-300 transition-colors group-hover:bg-primary-500/25">
                <i className={item.icon}></i>
              </span>
              <span className="min-w-0">
                <span className="block text-sm text-foreground-900">{item.label}</span>
                <span className="block text-[11px] text-foreground-500">{item.desc}</span>
              </span>
            </Link>
          ))}
        </section>
      </div>

      <div className="container-page mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_336px]">
        <div className="min-w-0">
          <section>
            <SectionTitle
              eyebrow="Latest Videos"
              title="最新上线"
              description="按上传时间整理的新片，涵盖剧情、情侣、唯美与独立制片。"
              moreLabel="进入视频分类"
              moreTo="/video-category"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {videos.slice(0, 6).map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </section>

          <section className="mt-12">
            <SectionTitle
              eyebrow="Photo Stories"
              title="图片精选"
              description="以光影、质感与留白为主的写真图集，点击可进入大图浏览。"
              moreLabel="全部图集"
              moreTo="/image-category"
            />
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {featuredGalleries.map((gallery) => (
                <GalleryCard key={gallery.id} gallery={gallery} />
              ))}
            </div>
          </section>

          <section className="mt-12">
            <SectionTitle
              eyebrow="Newsroom"
              title="最新资讯"
              description="行业观察、内容分级讨论与夜间用眼的小知识。"
              moreLabel="全部资讯"
              moreTo="/news-category"
            />
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-2">
              {featuredNews.map((article) => (
                <NewsCard key={article.id} article={article} />
              ))}
              <div className="border-t border-background-200/80 px-3 py-3">
                <Link
                  to="/news-category"
                  className="flex items-center gap-1 text-xs text-foreground-500 transition-colors hover:text-accent-500"
                >
                  查看全部资讯
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>
        </div>

        <aside className="min-w-0 space-y-6">
          <section className="panel overflow-hidden">
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3.5">
              <h2 className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                <i className="ri-fire-line text-accent-500"></i>
                热门推荐
              </h2>
              <Link
                to="/video-category"
                className="text-[11px] text-foreground-500 transition-colors hover:text-accent-500"
              >
                更多
              </Link>
            </div>
            <div className="p-2">
              {popularVideos.map((video, index) => (
                <Link
                  key={video.id}
                  to={`/video-detail/${video.id}`}
                  className="group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-200/50"
                >
                  <span
                    className={`w-4 shrink-0 text-center font-mono text-xs ${
                      index < 3 ? "text-accent-500" : "text-foreground-500"
                    }`}
                  >
                    {index + 1}
                  </span>
                  <div className="h-[52px] w-[88px] shrink-0 overflow-hidden rounded bg-background-200">
                    <img
                      src={video.cover}
                      alt={`${video.title} - 热门视频封面`}
                      title={`${video.title} 成人视频`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs text-foreground-900 transition-colors group-hover:text-accent-500">
                      {video.title}
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-[10px] text-foreground-500">
                      <span>{video.category}</span>
                      <span className="flex items-center gap-0.5">
                        <i className="ri-play-circle-line"></i>
                        {formatViews(video.views)}
                      </span>
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <section className="panel overflow-hidden">
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3.5">
              <h2 className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                <i className="ri-chat-smile-2-line text-accent-500"></i>
                社区热帖
              </h2>
              <Link
                to="/community"
                className="text-[11px] text-foreground-500 transition-colors hover:text-accent-500"
              >
                进入社区
              </Link>
            </div>
            <div className="px-3 py-1">
              {hotTopics.map((topic) => (
                <TopicRow key={topic.id} topic={topic} />
              ))}
            </div>
          </section>

          <section className="panel overflow-hidden">
            <div className="border-b border-background-200 px-4 py-3.5">
              <h2 className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                <i className="ri-compass-3-line text-accent-500"></i>
                专题导航
              </h2>
            </div>
            <div className="p-2">
              {columns.map((column) => (
                <Link
                  key={column.label}
                  to="/video-category"
                  className="group flex items-center justify-between rounded-md px-2.5 py-2.5 transition-colors hover:bg-background-200/50"
                >
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-900 group-hover:text-accent-500">
                      {column.label}
                    </span>
                    <span className="mt-0.5 block truncate text-[10px] text-foreground-500">
                      {column.desc}
                    </span>
                  </span>
                  <i className="ri-arrow-right-up-line text-foreground-600 transition-colors group-hover:text-accent-500"></i>
                </Link>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-primary-800/60 bg-primary-950/40 p-5">
            <h2 className="text-sm font-medium text-foreground-950">夜间观看提示</h2>
            <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
              建议保留一盏低亮度背景灯，并开启设备暖色模式。每观看 40 分钟起身活动，眼睛会感谢你。
            </p>
            <Link
              to="/news-detail/n3"
              className="mt-3 inline-flex items-center gap-1 text-[11px] text-accent-500 hover:underline"
            >
              阅读完整建议
              <i className="ri-arrow-right-line"></i>
            </Link>
          </section>
        </aside>
      </div>

      <section className="container-page mt-14">
        <div className="rounded-xl border border-background-200 bg-background-100 px-6 py-8 md:px-9">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-center">
            <div>
              <span className="text-[11px] uppercase tracking-[0.28em] text-accent-500">
                About Adult Net
              </span>
              <h2 className="mt-3 font-heading text-2xl text-foreground-950">
                一个安静的成人内容门户
              </h2>
              <p className="mt-3 max-w-2xl text-[13px] leading-relaxed text-foreground-500">
                成人网把内容整理成视频、图集、资讯与社区四条线索，尽量减少打扰式的推荐，
                让你按自己的节奏浏览。我们坚持适龄提示、隐私最小化与克制的内容呈现。
              </p>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-2 text-xs text-accent-500 hover:underline"
              >
                了解我们的定位与服务范围
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-200 bg-background-50 px-3 py-4 text-center"
                >
                  <p className="font-heading text-lg text-accent-500 md:text-xl">{stat.value}</p>
                  <p className="mt-1 text-[10px] text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}